// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { getMyCapabilities } from "./meCapabilitiesApi";
import type {
  MeCapabilitiesApiContext,
  MeCapabilitiesModel,
} from "./meCapabilitiesApi";

interface ResolvedCapabilities {
  key: string;
  model: MeCapabilitiesModel;
}

interface InFlightCapabilities {
  key: string;
  promise: Promise<MeCapabilitiesModel>;
}

let resolved: ResolvedCapabilities | undefined;
let inFlight: InFlightCapabilities | undefined;

function cacheKey(context: MeCapabilitiesApiContext): string {
  const mode = context.allowTestModeRequests === true ? "test" : "live";
  return `${mode}:${context.token ?? ""}`;
}

export function readCapabilities(
  context: MeCapabilitiesApiContext,
  options?: { force?: boolean },
): Promise<MeCapabilitiesModel> {
  const key = cacheKey(context);
  const force = options?.force === true;

  if (!force) {
    if (resolved !== undefined && resolved.key === key) {
      return Promise.resolve(resolved.model);
    }

    if (inFlight !== undefined && inFlight.key === key) {
      return inFlight.promise;
    }
  }

  const request = getMyCapabilities(context);
  request.then(
    (model) => {
      resolved = { key, model };
      if (inFlight !== undefined && inFlight.key === key) {
        inFlight = undefined;
      }
    },
    () => {
      if (inFlight !== undefined && inFlight.key === key) {
        inFlight = undefined;
      }
    },
  );

  inFlight = { key, promise: request };
  return request;
}

export function resetCapabilitiesRead(): void {
  resolved = undefined;
  inFlight = undefined;
}
